import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  Users, 
  CheckCircle2, 
  AlertCircle,
  Edit,
  Send,
  Download,
  Eye,
  PenTool,
  Calendar,
  DollarSign,
  User,
  MessageSquare,
  X,
  UserPlus,
  Trash2,
  Plus,
  Activity,
  Package,
  Folder,
  Play,
  Pause,
  Timer,
  MoreHorizontal
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import BriefForm from '../../components/projects/BriefForm';
import BriefView from '../../components/projects/BriefView';
import ProjectChat from '../../components/chat/ProjectChat';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import ChronoLauncher from '../../components/timer/ChronoLauncher';

// Inline Messages Panel Component
const ProjectMessagesPanel = ({ projectId, theme, user }) => {
  const { isClient } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  React.useEffect(() => {
    if (projectId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [projectId]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/messages`);
      let msgList = res.data || [];
      
      // Filter out action_request messages for clients
      if (isClient) {
        msgList = msgList.filter(msg => msg.message_type !== 'action_request');
      }
      
      setMessages(msgList);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/messages`, {
        content: newMessage,
        message_type: 'text'
      });
      setNewMessage('');
      await fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const getRoleColor = (role) => {
    if (role === 'agency') return 'bg-indigo-100 text-indigo-700';
    if (role === 'client') return 'bg-emerald-100 text-emerald-700';
    return 'bg-blue-100 text-blue-700';
  };

  const getRoleLabel = (role) => {
    if (role === 'agency') return 'Agence';
    if (role === 'client') return 'Client';
    return 'Équipe';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[500px]">
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-[#64748b]">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun message pour le moment</p>
            <p className="text-xs mt-1">Soyez le premier à envoyer un message !</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === user?.id;
            return (
              <div
                key={msg.id}
                className={cn(
                  "flex gap-3",
                  isOwn ? "flex-row-reverse" : ""
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0",
                  msg.sender_role === 'agency' ? "bg-indigo-500" :
                  msg.sender_role === 'client' ? "bg-emerald-500" : "bg-blue-500"
                )}>
                  {msg.sender_name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                
                {/* Message Content */}
                <div className={cn("max-w-[75%]", isOwn && "text-right")}>
                  <div className={cn("flex items-center gap-2 mb-1", isOwn && "flex-row-reverse")}>
                    <span className="text-xs font-medium text-[#334155]">
                      {msg.sender_name}
                    </span>
                    <Badge className={cn("text-[10px] px-1.5 py-0", getRoleColor(msg.sender_role))}>
                      {getRoleLabel(msg.sender_role)}
                    </Badge>
                    <span className="text-[10px] text-[#94a3b8]">
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                  
                  <div className={cn(
                    "p-3 rounded-xl text-sm",
                    isOwn 
                      ? "bg-[#6366f1] text-white rounded-br-sm" 
                      : "bg-[#f1f5f9] text-[#334155] rounded-bl-sm"
                  )}>
                    {msg.content}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input Area */}
      <div className="border-t border-[#e2e8f0] pt-4">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Écrivez votre message..."
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button 
            onClick={handleSend}
            disabled={sending || !newMessage.trim()}
            style={{ backgroundColor: theme.colors?.accent }}
            className="rounded-full"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isAgency, isClient, isTeamMember } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [brief, setBrief] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [finances, setFinances] = useState({
    totalCost: 0,
    teamCost: 0,
    expensesCost: 0,
    margin: 0,
    margin_percentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [showBriefForm, setShowBriefForm] = useState(false);
  const [showEstimationModal, setShowEstimationModal] = useState(false);
  const [estimation, setEstimation] = useState({ hours: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showChat, setShowChat] = useState(false);
  
  // Team management states
  const [showAssignManagerModal, setShowAssignManagerModal] = useState(false);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedTeamMemberIds, setSelectedTeamMemberIds] = useState([]);
  const [projectManager, setProjectManager] = useState(null);
  const [assignedTeamDetails, setAssignedTeamDetails] = useState([]);
  
  // Task management states
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    assigned_to: '',
    estimated_hours: '',
    deadline: '',
    max_versions: 3
  });
  
  // Timeline/Activity states
  const [projectActivity, setProjectActivity] = useState([]);
  
  // Edit project states
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({
    name: '',
    description: '',
    estimated_hours: '',
    deadline: '',
    status: ''
  });

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, briefRes, tasksRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/briefs/project/${projectId}`).catch(() => ({ data: null })),
        api.get(`/tasks/project/${projectId}`).catch(() => ({ data: [] }))
      ]);
      
      setProject(projectRes.data);
      setBrief(briefRes.data);
      setTasks(tasksRes.data || []);

      if (isAgency) {
        const [teamRes, financesRes] = await Promise.all([
          api.get('/users/', { params: { role: 'team_member' } }),
          api.get(`/projects/${projectId}/finances`).catch(() => ({ data: null }))
        ]);
        setTeamMembers(teamRes.data);
        // Only update finances if we got valid data
        if (financesRes.data) {
          setFinances({
            totalCost: financesRes.data.totalCost || financesRes.data.costs?.total || 0,
            teamCost: financesRes.data.teamCost || financesRes.data.costs?.internal_team || 0,
            expensesCost: financesRes.data.expensesCost || financesRes.data.costs?.expenses_total || 0,
            margin: financesRes.data.margin || 0,
            margin_percentage: financesRes.data.margin_percentage || 0
          });
        }
        
        // Fetch project manager details
        if (projectRes.data.account_manager_id) {
          const managerRes = await api.get(`/users/${projectRes.data.account_manager_id}`).catch(() => null);
          if (managerRes?.data) {
            setProjectManager(managerRes.data);
          }
        }
        
        // Fetch assigned team details
        if (projectRes.data.assigned_team?.length > 0) {
          const teamDetails = teamRes.data.filter(m => projectRes.data.assigned_team.includes(m.id));
          setAssignedTeamDetails(teamDetails);
        } else {
          setAssignedTeamDetails([]);
        }
        
        // Fetch project activity
        const activityRes = await api.get(`/projects/${projectId}/activity`).catch(() => ({ data: [] }));
        setProjectActivity(activityRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Erreur lors du chargement du projet');
    } finally {
      setLoading(false);
    }
  };

  const handleSignBrief = async () => {
    try {
      await api.post(`/briefs/${brief.id}/sign`);
      toast.success('Brief signé avec succès !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleValidateBAT = async () => {
    try {
      await api.put(`/projects/${projectId}/validate-bat`);
      toast.success('BAT validé avec succès !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  // Edit project functions
  const openEditProjectModal = () => {
    setEditProjectForm({
      name: project?.name || '',
      description: project?.description || '',
      estimated_hours: project?.estimated_hours?.toString() || '',
      deadline: project?.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '',
      status: project?.status || ''
    });
    setShowEditProjectModal(true);
  };

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      const updateData = {};
      if (editProjectForm.name && editProjectForm.name !== project.name) {
        updateData.name = editProjectForm.name;
      }
      if (editProjectForm.description !== project.description) {
        updateData.description = editProjectForm.description;
      }
      if (editProjectForm.estimated_hours && parseFloat(editProjectForm.estimated_hours) !== project.estimated_hours) {
        updateData.estimated_hours = parseFloat(editProjectForm.estimated_hours);
      }
      if (editProjectForm.deadline) {
        updateData.deadline = editProjectForm.deadline;
      }
      if (editProjectForm.status && editProjectForm.status !== project.status) {
        updateData.status = editProjectForm.status;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('Aucune modification détectée');
        setShowEditProjectModal(false);
        return;
      }

      await api.put(`/projects/${projectId}`, updateData);
      toast.success('Projet mis à jour avec succès !');
      setShowEditProjectModal(false);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEstimation = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}`, {
        estimated_hours: parseFloat(estimation.hours),
        estimation_notes: estimation.notes
      });
      toast.success('Estimation mise à jour !');
      setShowEstimationModal(false);
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Team management functions
  const handleAssignManager = async () => {
    if (!selectedManagerId) {
      toast.error('Veuillez sélectionner un chef de projet');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/assign-manager`, { manager_id: selectedManagerId });
      toast.success('Chef de projet assigné !');
      setShowAssignManagerModal(false);
      setSelectedManagerId('');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTeamMember = async () => {
    if (selectedTeamMemberIds.length === 0) {
      toast.error('Veuillez sélectionner au moins un membre');
      return;
    }
    setSaving(true);
    try {
      // Combine existing team with new selections
      const currentTeam = project.assigned_team || [];
      const newTeam = [...new Set([...currentTeam, ...selectedTeamMemberIds])];
      await api.put(`/projects/${projectId}/assign-team`, { team_member_ids: newTeam });
      toast.success('Équipe mise à jour !');
      setShowAssignTeamModal(false);
      setSelectedTeamMemberIds([]);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignMember = async (memberId) => {
    if (!window.confirm('Retirer ce membre du projet ?')) return;
    try {
      await api.delete(`/projects/${projectId}/unassign/${memberId}`);
      toast.success('Membre retiré du projet');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors du retrait');
    }
  };

  const toggleTeamMemberSelection = (memberId) => {
    setSelectedTeamMemberIds(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Task management functions
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.name.trim()) {
      toast.error('Le nom de la tâche est requis');
      return;
    }
    setSaving(true);
    try {
      await api.post('/tasks/', {
        project_id: projectId,
        name: newTask.name,
        description: newTask.description || null,
        assigned_to: newTask.assigned_to || null,
        estimated_hours: newTask.estimated_hours ? parseFloat(newTask.estimated_hours) : null,
        deadline: newTask.deadline || null
      });
      toast.success('Tâche créée !');
      setShowCreateTaskModal(false);
      setNewTask({ name: '', description: '', assigned_to: '', estimated_hours: '', deadline: '' });
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}/status`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchProjectData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSubmitVersion = async (taskId) => {
    const notes = window.prompt('Notes pour cette version (optionnel):');
    if (notes === null) return; // User cancelled
    
    try {
      await api.post(`/tasks/${taskId}/submit-version`, { notes: notes || '' });
      toast.success('Version soumise pour validation client !');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    }
  };

  const handleClientResponse = async (taskId, response) => {
    let notes = '';
    if (response === 'rejected' || response === 'accepted_with_reservations') {
      notes = window.prompt(
        response === 'rejected' 
          ? 'Raison du refus:' 
          : 'Vos réserves:'
      );
      if (notes === null) return; // User cancelled
    }
    
    try {
      await api.post(`/tasks/${taskId}/client-response`, { response, notes });
      const labels = {
        accepted: 'Tâche acceptée !',
        rejected: 'Tâche refusée',
        accepted_with_reservations: 'Tâche acceptée avec réserves'
      };
      toast.success(labels[response]);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      brief_pending: 'bg-yellow-100 text-yellow-700',
      brief_signed: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      review: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      draft: 'Brouillon',
      brief_pending: 'Brief en attente',
      brief_signed: 'Brief validé',
      in_progress: 'En cours',
      review: 'En révision',
      delivered: 'Livré',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.draft)}>
        {labels[status] || status}
      </Badge>
    );
  };

  // =============================================
  // GÉNÉRATION DE RAPPORT PDF DE PROJET
  // =============================================
  const generateProjectReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(26, 54, 93); // Navy
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DE PROJET', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(project.name || 'Sans nom', pageWidth / 2, 32, { align: 'center' });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    let yPos = 55;
    
    // Project Info Section
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Informations générales', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const projectInfo = [
      ['Client', project.client_name || 'N/A'],
      ['Type', projectTypeLabels[project.project_type] || project.project_type],
      ['Statut', statusConfig[project.status]?.label || project.status],
      ['Date de création', project.created_at ? new Date(project.created_at).toLocaleDateString('fr-FR') : 'N/A'],
      ['Date de livraison', project.deadline ? new Date(project.deadline).toLocaleDateString('fr-FR') : 'N/A'],
    ];
    
    projectInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 60, yPos);
      yPos += 6;
    });
    
    yPos += 10;
    
    // Financial Summary
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Résumé financier', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    const financialInfo = [
      ['Heures estimées', `${project.estimated_hours || 0}h`],
      ['Heures réelles', `${project.actual_hours || 0}h`],
      ['Taux horaire', `${project.hourly_rate || 75}€/h`],
      ['Budget', `${calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€`],
      ['Coût réel', `${finances.totalCost.toLocaleString('fr-FR')}€`],
      ['Marge', `${finances.margin.toLocaleString('fr-FR')}€ (${finances.margin_percentage.toFixed(1)}%)`],
    ];
    
    financialInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${label}: `, 14, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value), 60, yPos);
      yPos += 6;
    });
    
    yPos += 10;
    
    // Tasks Table
    if (tasks.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Tâches du projet', 14, yPos);
      yPos += 5;
      
      const taskData = tasks.map(task => [
        task.title || 'Sans titre',
        task.assignee_name || 'Non assigné',
        task.status === 'completed' ? 'Terminée' : task.status === 'in_progress' ? 'En cours' : 'À faire',
        `${task.actual_hours || 0}h / ${task.estimated_hours || 0}h`,
        task.completed_at ? new Date(task.completed_at).toLocaleDateString('fr-FR') : '-'
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Tâche', 'Responsable', 'Statut', 'Temps', 'Terminée le']],
        body: taskData,
        theme: 'striped',
        headStyles: { fillColor: [26, 54, 93] },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
    }
    
    // Time Entries Table
    if (timeEntries.length > 0 && yPos < 250) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Chronos enregistrés', 14, yPos);
      yPos += 5;
      
      const timeData = timeEntries.slice(0, 10).map(entry => [
        entry.description || 'Sans description',
        entry.user_name || 'N/A',
        `${(entry.duration / 60).toFixed(1)}h`,
        entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : '-'
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Description', 'Membre', 'Durée', 'Date']],
        body: timeData,
        theme: 'striped',
        headStyles: { fillColor: [26, 54, 93] },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')} - Page ${i}/${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `rapport_${project.name?.replace(/[^a-z0-9]/gi, '_') || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Rapport PDF généré avec succès');
  };

  const projectTypeLabels = {
    logo: 'Création de Logo',
    visual: 'Création Visuelle',
    video: 'Production Vidéo',
    website: 'Site Internet',
    mobile_app: 'Application Mobile',
  };

  const projectTypeIcons = {
    logo: '🎨',
    visual: '🖼️',
    video: '🎬',
    website: '🌐',
    mobile_app: '📱',
  };

  const calculateBudget = (hours) => {
    const hourlyRate = project?.hourly_rate || 75;
    return hours * hourlyRate;
  };

  const progressPercent = project?.estimated_hours > 0 
    ? Math.min((project.actual_hours / project.estimated_hours) * 100, 100) 
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2">Projet non trouvé</h2>
          <Button onClick={() => navigate(-1)}>Retour</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Back Button & Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <span className="text-3xl">{projectTypeIcons[project.project_type]}</span>
                <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                  {project.name}
                </h1>
              </div>
              <div className="flex items-center gap-3 mt-2">
                {getStatusBadge(project.status)}
                <Badge variant="outline">{projectTypeLabels[project.project_type]}</Badge>
                {project.client_name && (
                  <span className="text-sm text-[#64748b] flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {project.client_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {isAgency && (
              <>
                <Button 
                  variant="outline"
                  onClick={generateProjectReport}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Rapport PDF
                </Button>
                <Button 
                  variant="outline"
                  onClick={openEditProjectModal}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </>
            )}
            {isClient && project.status === 'delivered' && !project.bat_validated && (
              <Button 
                onClick={handleValidateBAT}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Valider le BAT
              </Button>
            )}
            {isAgency && project.status === 'draft' && (
              <Button 
                onClick={() => setShowBriefForm(true)}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Créer le brief
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowChat(true)}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${theme.colors?.accent}20` }}>
                  <Clock className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Heures</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {project.actual_hours?.toFixed(1) || 0}h / {project.estimated_hours || 0}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget Card - Show hours for clients/creatives, euros for agency */}
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                  {isAgency ? (
                    <DollarSign className="w-5 h-5 text-green-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-green-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Budget</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {isAgency 
                      ? `${calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€`
                      : `${project.estimated_hours || 0}h`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spent Card - Show hours for clients/creatives, euros for agency */}
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100">
                  {isAgency ? (
                    <DollarSign className="w-5 h-5 text-orange-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-orange-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">{isAgency ? 'Dépensé' : 'Consommé'}</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {isAgency 
                      ? `${(project.spent || 0).toLocaleString('fr-FR')}€`
                      : `${(project.actual_hours || 0).toFixed(1)}h`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Margin Card - Agency Only - Enhanced */}
          {isAgency && finances && (
            <Card className={cn("card col-span-1 md:col-span-2", finances.margin >= 0 ? "border-emerald-200 bg-emerald-50/30" : "border-red-200 bg-red-50/30")}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center",
                      finances.margin >= 0 ? "bg-emerald-100" : "bg-red-100"
                    )}>
                      <DollarSign className={cn(
                        "w-6 h-6",
                        finances.margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )} />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748b] mb-0.5">Marge du projet</p>
                      <p className={cn(
                        "font-mono font-bold text-xl",
                        finances.margin >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {finances.margin >= 0 ? '+' : ''}{finances.margin.toLocaleString('fr-FR')}€
                        <span className="text-sm ml-2 font-normal">({finances.margin_percentage.toFixed(1)}%)</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Margin Calculation Details */}
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Budget</p>
                      <p className="font-semibold text-[#1a365d]">{calculateBudget(project.estimated_hours || 0).toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">−</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Coût équipe</p>
                      <p className="font-semibold text-amber-600">{finances.teamCost.toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">−</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Dépenses</p>
                      <p className="font-semibold text-red-500">{finances.expensesCost.toLocaleString('fr-FR')}€</p>
                    </div>
                    <span className="text-[#64748b]">=</span>
                    <div className="text-center">
                      <p className="text-xs text-[#64748b]">Marge</p>
                      <p className={cn("font-bold", finances.margin >= 0 ? "text-emerald-600" : "text-red-600")}>
                        {finances.margin.toLocaleString('fr-FR')}€
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#64748b]">Équipe</p>
                  <p className="font-mono font-bold" style={{ color: theme.colors?.primary }}>
                    {project.assigned_team?.length || 0} membre(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: theme.colors?.primary }}>Progression</span>
              <span className="text-sm font-mono text-[#64748b]">{progressPercent.toFixed(0)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="brief" className="space-y-6">
          <TabsList className="bg-white border flex-wrap">
            <TabsTrigger value="brief" className="gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Brief</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Tâches</span>
            </TabsTrigger>
            <TabsTrigger value="timeline" className="gap-2">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Suivi</span>
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Équipe</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Messages</span>
            </TabsTrigger>
          </TabsList>

          {/* Brief Tab */}
          <TabsContent value="brief">
            {brief ? (
              <BriefView 
                brief={brief} 
                project={project}
                onSign={handleSignBrief}
                isAgency={isAgency}
                isClient={isClient}
                theme={theme}
              />
            ) : (
              <Card className="card">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
                  <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                    Aucun brief créé
                  </h3>
                  <p className="text-[#64748b] mb-4">
                    Le brief client doit être créé pour démarrer le projet
                  </p>
                  {isAgency && (
                    <Button 
                      onClick={() => setShowBriefForm(true)}
                      style={{ backgroundColor: theme.colors?.accent }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Créer le brief
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle className="text-base">Tâches ({tasks.length})</CardTitle>
                {isAgency && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCreateTaskModal(true)}
                    className="h-8"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {tasks.length === 0 ? (
                  <div className="text-center py-8 text-[#64748b]">
                    <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune tâche</p>
                    {isAgency && (
                      <Button 
                        variant="link" 
                        size="sm"
                        className="mt-1"
                        onClick={() => setShowCreateTaskModal(true)}
                      >
                        Créer la première tâche
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                          <TableHead className="w-[40%] text-xs font-medium">Tâche</TableHead>
                          <TableHead className="text-xs font-medium">Assigné</TableHead>
                          <TableHead className="text-xs font-medium text-center">Temps</TableHead>
                          <TableHead className="text-xs font-medium text-center">Version</TableHead>
                          <TableHead className="text-xs font-medium">Statut</TableHead>
                          <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tasks.map((task) => (
                          <TableRow 
                            key={task.id}
                            className={cn(
                              "cursor-pointer transition-colors",
                              task.status === 'awaiting_client' && "bg-purple-50/30",
                              task.status === 'completed' && "bg-green-50/30"
                            )}
                          >
                            <TableCell className="py-2">
                              <div className="min-w-0">
                                <span 
                                  className="font-medium text-sm text-slate-800 truncate hover:text-cyan-600 hover:underline cursor-pointer block"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTask(task);
                                  }}
                                  role="button"
                                  tabIndex={0}
                                >
                                  {task.name}
                                </span>
                                {task.deadline && (
                                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(task.deadline).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              <span className="text-sm text-slate-600">{task.assigned_to_name || '-'}</span>
                            </TableCell>
                            <TableCell className="py-2 text-center">
                              <span className="text-xs font-mono text-slate-600">
                                {task.actual_hours?.toFixed(1) || 0}h/{task.estimated_hours || 0}h
                              </span>
                            </TableCell>
                            <TableCell className="py-2 text-center">
                              <Badge variant="outline" className="text-[10px] h-5">
                                V{task.current_version || 0}/{task.max_versions || 3}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2">
                              <Badge className={cn(
                                "text-[10px] h-5",
                                task.status === 'completed' && 'bg-green-100 text-green-700',
                                task.status === 'awaiting_client' && 'bg-purple-100 text-purple-700',
                                task.status === 'in_progress' && 'bg-blue-100 text-blue-700',
                                task.status === 'rejected' && 'bg-red-100 text-red-700',
                                task.status === 'pending' && 'bg-slate-100 text-slate-700'
                              )}>
                                {task.status === 'completed' ? 'Terminée' : 
                                 task.status === 'awaiting_client' ? 'Validation' :
                                 task.status === 'in_progress' ? 'En cours' : 
                                 task.status === 'rejected' ? 'Rejetée' :
                                 'En attente'}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {/* Chrono launcher for agency */}
                                {isAgency && (task.status === 'in_progress' || task.status === 'pending') && (
                                  <ChronoLauncher
                                    taskId={task.id}
                                    projectId={projectId}
                                    taskTitle={task.name}
                                    size="sm"
                                    variant="ghost"
                                    showLabel={false}
                                  />
                                )}
                                {/* Add time button for agency */}
                                {isAgency && (task.status === 'in_progress' || task.status === 'pending') && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-500 hover:text-blue-600"
                                    title="Ajouter du temps"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedTask({...task, _addTime: true});
                                    }}
                                  >
                                    <Timer className="w-3 h-3" />
                                  </Button>
                                )}
                                
                                {/* Submit button for agency */}
                                {isAgency && task.status === 'in_progress' && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-slate-500 hover:text-purple-600"
                                    title="Soumettre pour validation"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubmitVersion(task.id);
                                    }}
                                  >
                                    <Send className="w-3 h-3" />
                                  </Button>
                                )}
                                
                                {/* Client validation buttons */}
                                {isClient && task.status === 'awaiting_client' && (
                                  <>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-red-500 hover:bg-red-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientResponse(task.id, 'rejected');
                                      }}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 text-green-600 hover:bg-green-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleClientResponse(task.id, 'accepted');
                                      }}
                                    >
                                      <CheckCircle2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                
                                {/* More options dropdown for agency */}
                                {isAgency && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="icon" variant="ghost" className="h-7 w-7">
                                        <MoreHorizontal className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => setSelectedTask(task)}>
                                        <Edit className="w-4 h-4 mr-2" /> Modifier
                                      </DropdownMenuItem>
                                      {task.status !== 'completed' && task.status !== 'awaiting_client' && (
                                        <>
                                          <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'pending')}>
                                            <Clock className="w-4 h-4 mr-2" /> En attente
                                          </DropdownMenuItem>
                                          <DropdownMenuItem onClick={() => handleUpdateTaskStatus(task.id, 'in_progress')}>
                                            <Play className="w-4 h-4 mr-2" /> En cours
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline">
            <Card className="card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Suivi du projet</CardTitle>
                  <CardDescription>Timeline des activités et livrables</CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {projectActivity.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune activité enregistrée</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
                    
                    <div className="space-y-4">
                      {projectActivity.map((activity, idx) => {
                        const iconMap = {
                          'folder': Folder,
                          'file-text': FileText,
                          'check-square': CheckCircle2,
                          'send': Send,
                          'message-circle': MessageSquare,
                          'package': Package
                        };
                        const IconComponent = iconMap[activity.icon] || Activity;
                        
                        const colorMap = {
                          'project_created': 'bg-blue-100 text-blue-600',
                          'brief_submitted': 'bg-cyan-100 text-cyan-600',
                          'task_created': 'bg-slate-100 text-slate-600',
                          'version_submitted': 'bg-purple-100 text-purple-600',
                          'client_accepted': 'bg-green-100 text-green-600',
                          'client_rejected': 'bg-red-100 text-red-600',
                          'client_accepted_with_reservations': 'bg-yellow-100 text-yellow-600',
                          'deliverable_added': 'bg-emerald-100 text-emerald-600'
                        };
                        const colorClass = colorMap[activity.type] || 'bg-slate-100 text-slate-600';
                        
                        return (
                          <div key={activity.id} className="relative flex gap-4 pl-2">
                            {/* Icon */}
                            <div className={cn(
                              "relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                              colorClass
                            )}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 pb-4">
                              <div className="bg-white rounded-lg border border-slate-100 p-3 shadow-sm">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
                                  <p className="font-medium text-sm text-slate-800">{activity.title}</p>
                                  <span className="text-xs text-slate-400">
                                    {activity.timestamp 
                                      ? new Date(activity.timestamp).toLocaleDateString('fr-FR', { 
                                          day: 'numeric', 
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : '-'
                                    }
                                  </span>
                                </div>
                                {activity.description && (
                                  <p className="text-xs text-slate-500 line-clamp-2">{activity.description}</p>
                                )}
                                {activity.file_url && (
                                  <a 
                                    href={activity.file_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-cyan-600 hover:underline mt-2"
                                  >
                                    <Download className="w-3 h-3" />
                                    Télécharger
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team">
            <div className="space-y-6">
              {/* Project Manager Card */}
              <Card className="card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Chef de projet</CardTitle>
                    <CardDescription>Responsable du suivi client et de la coordination</CardDescription>
                  </div>
                  {isAgency && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAssignManagerModal(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      {project.account_manager_id ? 'Modifier' : 'Assigner'}
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {projectManager ? (
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                        {projectManager.first_name?.[0]}{projectManager.last_name?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">
                          {projectManager.first_name} {projectManager.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{projectManager.email}</p>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700">Chef de projet</Badge>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun chef de projet assigné</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Team Members Card */}
              <Card className="card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Membres de l'équipe</CardTitle>
                    <CardDescription>Créatifs et collaborateurs sur ce projet</CardDescription>
                  </div>
                  {isAgency && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowAssignTeamModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {assignedTeamDetails.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucun membre assigné</p>
                      {isAgency && (
                        <Button 
                          variant="link" 
                          className="mt-2"
                          onClick={() => setShowAssignTeamModal(true)}
                        >
                          Assigner des membres
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {assignedTeamDetails.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-semibold text-sm">
                              {member.first_name?.[0]}{member.last_name?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">
                                {member.first_name} {member.last_name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {member.team_member_type === 'internal' ? 'Créatif interne' : 
                                 member.team_member_type === 'external' ? 'Créatif externe' : 'Membre'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isAgency && (
                              <Badge variant="outline" className="text-xs">
                                {member.hourly_rate || 0}€/h
                              </Badge>
                            )}
                            {isAgency && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleUnassignMember(member.id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Messages & Commentaires
                </CardTitle>
                <CardDescription>Échangez avec l'équipe projet</CardDescription>
              </CardHeader>
              <CardContent>
                <ProjectMessagesPanel 
                  projectId={projectId} 
                  theme={theme}
                  user={user}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Brief Form Dialog */}
        <Dialog open={showBriefForm} onOpenChange={setShowBriefForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="brief-form-description">
            <DialogHeader>
              <DialogTitle>Créer le brief client</DialogTitle>
              <p id="brief-form-description" className="text-sm text-[#64748b]">
                Remplissez le cahier des charges pour définir les attentes du projet
              </p>
            </DialogHeader>
            <BriefForm 
              projectId={projectId}
              projectType={project.project_type}
              onSuccess={() => {
                setShowBriefForm(false);
                fetchProjectData();
              }}
              onCancel={() => setShowBriefForm(false)}
              theme={theme}
            />
          </DialogContent>
        </Dialog>

        {/* Estimation Modal */}
        <Dialog open={showEstimationModal} onOpenChange={setShowEstimationModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Estimation du projet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Heures estimées</Label>
                <Input
                  type="number"
                  value={estimation.hours}
                  onChange={(e) => setEstimation({ ...estimation, hours: e.target.value })}
                  placeholder="Ex: 20"
                  className="input-default"
                />
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: `${theme.colors?.accent}10` }}>
                <p className="text-sm text-[#64748b]">Budget estimé</p>
                <p className="text-2xl font-mono font-bold" style={{ color: theme.colors?.primary }}>
                  {calculateBudget(parseFloat(estimation.hours) || 0).toLocaleString('fr-FR')}€
                </p>
                <p className="text-xs text-[#64748b]">Taux horaire: {project.hourly_rate || 75}€/h</p>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={estimation.notes}
                  onChange={(e) => setEstimation({ ...estimation, notes: e.target.value })}
                  placeholder="Notes sur l'estimation..."
                  className="input-default"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEstimationModal(false)}>Annuler</Button>
              <Button 
                onClick={handleUpdateEstimation}
                disabled={saving}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Manager Modal */}
        <Dialog open={showAssignManagerModal} onOpenChange={setShowAssignManagerModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Assigner un chef de projet</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sélectionner le responsable</Label>
                <Select value={selectedManagerId} onValueChange={setSelectedManagerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un membre..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                        {member.team_member_type === 'internal' && ' (Interne)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm text-slate-500">
                Le chef de projet est responsable du suivi client et de la coordination de l'équipe.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignManagerModal(false)}>Annuler</Button>
              <Button 
                onClick={handleAssignManager}
                disabled={saving || !selectedManagerId}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Assignation...' : 'Assigner'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Team Members Modal */}
        <Dialog open={showAssignTeamModal} onOpenChange={setShowAssignTeamModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Ajouter des membres à l'équipe</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Sélectionnez les membres à ajouter au projet.
              </p>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {teamMembers
                  .filter(m => !project?.assigned_team?.includes(m.id))
                  .map((member) => (
                    <div 
                      key={member.id} 
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedTeamMemberIds.includes(member.id) 
                          ? "border-cyan-500 bg-cyan-50" 
                          : "border-slate-200 hover:border-slate-300"
                      )}
                      onClick={() => toggleTeamMemberSelection(member.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-5 h-5 rounded border-2 flex items-center justify-center",
                          selectedTeamMemberIds.includes(member.id) 
                            ? "border-cyan-500 bg-cyan-500" 
                            : "border-slate-300"
                        )}>
                          {selectedTeamMemberIds.includes(member.id) && (
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white text-xs font-semibold">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {member.team_member_type === 'internal' ? 'Interne' : 
                             member.team_member_type === 'external' ? 'Externe' : 'Membre'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                {teamMembers.filter(m => !project?.assigned_team?.includes(m.id)).length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Tous les membres sont déjà assignés</p>
                  </div>
                )}
              </div>
              {selectedTeamMemberIds.length > 0 && (
                <div className="flex items-center gap-2 p-2 bg-cyan-50 rounded-lg">
                  <Badge className="bg-cyan-500">{selectedTeamMemberIds.length}</Badge>
                  <span className="text-sm text-cyan-700">membre(s) sélectionné(s)</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAssignTeamModal(false);
                setSelectedTeamMemberIds([]);
              }}>Annuler</Button>
              <Button 
                onClick={handleAssignTeamMember}
                disabled={saving || selectedTeamMemberIds.length === 0}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Ajout...' : `Ajouter ${selectedTeamMemberIds.length > 0 ? `(${selectedTeamMemberIds.length})` : ''}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvelle tâche</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom de la tâche *</Label>
                <Input
                  value={newTask.name}
                  onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                  placeholder="Ex: Création maquette Figma"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Détails de la tâche..."
                  className="min-h-[80px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Assigné à</Label>
                  <Select value={newTask.assigned_to || '_unassigned'} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v === '_unassigned' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_unassigned">Non assigné</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.first_name} {member.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heures estimées</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={newTask.estimated_hours}
                    onChange={(e) => setNewTask({ ...newTask, estimated_hours: e.target.value })}
                    placeholder="Ex: 8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={newTask.deadline}
                  onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateTaskModal(false)}>
                  Annuler
                </Button>
                <Button 
                  type="submit"
                  disabled={saving || !newTask.name.trim()}
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  {saving ? 'Création...' : 'Créer la tâche'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Task Modal */}
        <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier la tâche</DialogTitle>
            </DialogHeader>
            {selectedTask && (
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.put(`/tasks/${selectedTask.id}`, {
                    name: selectedTask.name,
                    description: selectedTask.description,
                    assigned_to: selectedTask.assigned_to || null,
                    estimated_hours: selectedTask.estimated_hours ? parseFloat(selectedTask.estimated_hours) : null,
                    deadline: selectedTask.deadline || null
                  });
                  toast.success('Tâche mise à jour !');
                  setSelectedTask(null);
                  fetchProjectData();
                } catch (error) {
                  toast.error('Erreur lors de la mise à jour');
                }
              }} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de la tâche *</Label>
                  <Input
                    value={selectedTask.name || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={selectedTask.description || ''}
                    onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                    className="min-h-[80px]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assigné à</Label>
                    <Select 
                      value={selectedTask.assigned_to || '_unassigned'} 
                      onValueChange={(v) => setSelectedTask({ ...selectedTask, assigned_to: v === '_unassigned' ? null : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_unassigned">Non assigné</SelectItem>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Heures estimées</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={selectedTask.estimated_hours || ''}
                      onChange={(e) => setSelectedTask({ ...selectedTask, estimated_hours: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Deadline</Label>
                    <Input
                      type="date"
                      value={selectedTask.deadline?.split('T')[0] || ''}
                      onChange={(e) => setSelectedTask({ ...selectedTask, deadline: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Heures réelles</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={selectedTask.actual_hours || ''}
                      onChange={(e) => setSelectedTask({ ...selectedTask, actual_hours: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setSelectedTask(null)}>
                    Annuler
                  </Button>
                  <Button type="submit" style={{ backgroundColor: theme.colors?.accent }}>
                    Enregistrer
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Project Modal */}
        <Dialog open={showEditProjectModal} onOpenChange={setShowEditProjectModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                Modifier le projet
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project_name">Nom du projet *</Label>
                <Input
                  id="project_name"
                  value={editProjectForm.name}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
                  placeholder="Nom du projet"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project_description">Description</Label>
                <Textarea
                  id="project_description"
                  value={editProjectForm.description}
                  onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
                  placeholder="Description du projet..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="project_hours">Heures estimées</Label>
                  <Input
                    id="project_hours"
                    type="number"
                    step="0.5"
                    value={editProjectForm.estimated_hours}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, estimated_hours: e.target.value })}
                    placeholder="40"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project_deadline">Deadline</Label>
                  <Input
                    id="project_deadline"
                    type="date"
                    value={editProjectForm.deadline}
                    onChange={(e) => setEditProjectForm({ ...editProjectForm, deadline: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project_status">Statut</Label>
                <Select 
                  value={editProjectForm.status} 
                  onValueChange={(value) => setEditProjectForm({ ...editProjectForm, status: value })}
                >
                  <SelectTrigger id="project_status">
                    <SelectValue placeholder="Sélectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="pending_brief">En attente de brief</SelectItem>
                    <SelectItem value="brief_sent">Brief envoyé</SelectItem>
                    <SelectItem value="brief_signed">Brief signé</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="review">En revue</SelectItem>
                    <SelectItem value="delivered">Livré</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="on_hold">En pause</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditProjectModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveProject} 
                disabled={saving || !editProjectForm.name}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Project Chat */}
        <ProjectChat 
          projectId={projectId}
          projectName={project?.name || 'Projet'}
          isOpen={showChat}
          onOpenChange={setShowChat}
        />
      </main>
    </div>
  );
};

export default ProjectDetailPage;
